import { FC, JSX, useEffect } from "react";
import "./Refunds.scss";
import MasterPage from "../../MasterPage/MasterPage";
import PageBase from "../../../components/PageBase/PageBase";
import SkeletonTable from "../../../components/SkeletonTable/SkeletonTable";
import AppPagination from "../../../components/AppPagination/AppPagination";
import { usePaginate } from "../../../providers/PaginateProvider";
import { useRefunds } from "../../../providers/Financial/Refunds/RefundsProvider";
import moment from "moment";
import FormatMoney from "../../../utils/FormatMoney";

interface RefundsProps {}

const Refunds: FC<RefundsProps> = () => {
  const {
    pages,
    refunds,
    params,
    setParams,
    isLoading,
    error,
  } = useRefunds();
  const { handlePaginate } = usePaginate();

  const getStatusBadgeClass = (status: string): string => {
    const s = status?.toLowerCase() ?? "";
    if (s.includes("reembolsado")) return "bg-success";
    if (s.includes("agendado")) return "bg-warning";
    return "bg-danger";
  };

  const renderList = (): JSX.Element => {
    return (
      <>
        {refunds?.length > 0 ? (
          <>
            {refunds.map((x: any) => (
              <tr key={x.id} className="text-sm">
                <td className="text-center">{x.id}</td>
                <td>
                  {x?.user ? (
                    <>
                      {x.user.partner_code && <strong>{x.user.partner_code}</strong>}
                      {x.user.partner_code && x.user.name ? " - " : ""}
                      {x.user.name ?? "-"}
                    </>
                  ) : "-"}
                </td>
                <td>{x?.plan?.name ?? "-"}</td>
                <td className="text-center">
                  {x?.plan?.cycle ? (x.plan.cycle === "annual" ? "Anual" : "Mensal") : "-"}
                </td>
                <td>
                  <b>{x?.amount_paid != null ? FormatMoney(x.amount_paid) : "-"}</b>
                </td>
                <td className="text-center">
                  {x?.created_at
                    ? moment(x.created_at).format("DD/MM/YYYY HH:mm")
                    : "-"}
                </td>
                <td className="text-center">
                  {x?.scheduled_end_date
                    ? moment(x.scheduled_end_date).format("DD/MM/YYYY HH:mm")
                    : "-"}
                </td>
                <td className="text-center">
                  {x?.status?.name ? (
                    <span className={`badge ${getStatusBadgeClass(x.status.name)}`}>
                      {x.status.name}
                    </span>
                  ) : "-"}
                </td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={8} className="text-center text-sm">
              Não há registros
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <>
        {!isLoading ? (
          <div>
            <div className="table-responsive">
              <table className="table table-flush m-0 w-100">
                <thead className="thead-light">
                  <tr>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      #
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Usuário
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Plano
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Tipo
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Valor
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Data de Solicitação
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Data Agendada
                    </th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>{renderList()}</tbody>
              </table>
            </div>

            {refunds?.length > 0 && (
              <div>
                <nav aria-label="...">
                  <AppPagination
                    pages={pages}
                    handlePaginate={handlePaginate}
                    params={params}
                    setParams={setParams}
                    style={{ backgroundColor: "#fff" }}
                  />
                </nav>
              </div>
            )}
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  useEffect(() => {
    setParams({ page: 1, perPage: 10, withPaginate: true });
  }, []);

  return (
    <MasterPage title="Reembolsos" icon="fas fa-rotate-left" footerFixed>
      <div className="Refunds" data-testid="Refunds">
        <PageBase
          title="Reembolsos"
          subtitle="Histórico de reembolsos das assinaturas"
          error={error}
          children={renderContent()}
        />
      </div>
    </MasterPage>
  );
};

export default Refunds;
